import mitt, { type Emitter } from 'mitt';
import { io } from 'socket.io-client';
import { AbortablePromise } from '@xuchaoqian/abortable-promise';
import { sleep } from '@/lib/util';

import {
  fetchPkceCredentials,
  fetchUserInfo,
  loginUrl,
} from '~/lib/streamlabs-api';
import { useToast } from '~/components/ui/toast';
import { deterministicUUIDv4 } from '~/lib/uuid';

import { generatePKCE } from '~/lib/security';
import Bindings, { openBrowserTo } from '~/lib/bindings';
import { reloadPluginDocks, restoreDocks } from '~/lib/docks';
import type { PDock } from '~/lib/obs/docks';
import { getMagicSessionUrlFor } from '~/lib/magic-tokens';

type StreamlabsWebsocketEvent =
  | { type: 'donation'; for: string; message: any }
  | { type: 'streamlabels'; message: { data: any } }
  | { type: 'streamlabs_prime_subscribe'; message: { data: any } }
  | { type: 'multistreamLive'; data: any }
  | { type: 'streamElementsImportComplete'; data: { settings_id: string } }
  | StreamlabsSocketEvents['account_permissions_required']
  | StreamlabsSocketEvents['account_merge_error']
  | StreamlabsSocketEvents['visionEvent']
  | StreamlabsSocketEvents['userStateUpdated'];

type StreamlabsWebsocketEventDonation = { for: string; message: any[] };
type StreamlabsWebsocketEventStreamlabels = {
  message: { [file: string]: string };
};

type GetInfoItem<T> = {
  is_live: boolean;
  chat_url: string;
  ccv: number;
  platform: T;
  platform_id: string;
  broadcast_id: string;
  channel_name: string;
};

export type MultistreamLive = {
  kick?: GetInfoItem<'kick'>;
  twitch?: GetInfoItem<'twitch'>;
  twitter?: GetInfoItem<'twitter'>;
  facebook?: GetInfoItem<'facebook'>;
  tiktok?: GetInfoItem<'tiktok'>;
  youtube?: GetInfoItem<'youtube'>;
  trovo?: GetInfoItem<'trovo'>;
  patreon?: GetInfoItem<'patreon'>;
};

export type StreamlabsSocketEvents = {
  event: StreamlabsWebsocketEvent;
  donation: StreamlabsWebsocketEventDonation;
  streamlabels: StreamlabsWebsocketEventStreamlabels;
  streamlabs_prime_subscribe: unknown;
  multistreamLive: MultistreamLive;
  streamElementsImportComplete: { settings_id: string };
  account_permissions_required: {
    type: 'account_permissions_required';
    event_id: string;
    for: string;
    message: {
      platform: 'twitch_account';
      url: string;
      _id: string;
      priority: number;
    }[];
  };
  account_merge_error: {
    type: 'account_merge_error';
    for: string;
    message: { platform: string; message: string; code: string };
  };
  // Reactive Overlays events
  visionEvent: { type: 'visionEvent'; event_id: string; message: unknown };
  userStateUpdated: {
    type: 'userStateUpdated';
    event_id: string;
    message: unknown;
  };
};

export type StreamlabsSocketEmitter = Emitter<StreamlabsSocketEvents>;

export type MagicLoginToken = string;

const useStore = defineStore(
  'auth',
  () => {
    console.debug('auth.store');
    const api = useApiStore();
    const uuid = ref<string>();

    // set our uuid to be the hashed fingerprint visitorId
    useFingerprint().then((fingerprint) => {
      uuid.value = deterministicUUIDv4(fingerprint.visitorId);
    });

    const emitter = mitt<StreamlabsSocketEvents>();
    emitter.on('*', (type, e) => console.debug('emitter', type, e));

    const { toast } = useToast();
    const app = useAppStore();
    const dialogs = useDialogsStore();
    const { track: $track } = useTrackingStore();
    const { public: $env } = useRuntimeConfig();
    const obs = useObsStore();
    const library = useLibraryStore();

    const authResponseParams = ref<AuthResponseParams | undefined>();
    const userInfo = ref<UserInfoResponse>();
    const donationSettings = ref<DonationSettingsResponse>();
    const authedAt = ref<number>();
    const expiresAt = ref<number>();

    const isBetaTester = ref(false);

    // if ($env.AUTH_RESPONSE_PARAMS) {
    //   setTimeout(() => {
    //     authResponseParams.value = JSON.parse($env.AUTH_RESPONSE_PARAMS);
    //     authedAt.value = Date.now();
    //   }, 500);
    // }

    const returnUrl = $env.AUTH_SUCCESS_RETURN_URL;

    const startResponseServer = async () => {
      const { port } = await Bindings.web.startServer(0, '/?', returnUrl);
      return port;
    };

    const logout = async () => {
      try {
        await api.auth.revokeToken();
      } finally {
        if (refreshTimeout) clearTimeout(refreshTimeout);
        authResponseParams.value = undefined;
      }
    };

    let abortablePromise: undefined | AbortablePromise<any>;

    let refreshTimeout: undefined | NodeJS.Timeout;

    const _doLoop = async <T extends Object>() => {
      let queryString = await Bindings.web
        .getAuthToken()
        .then(({ token }) => token);

      while (!queryString) {
        await sleep(500);
        queryString = await Bindings.web.getAuthToken().then(({ token }) => {
          return token;
        });
      }

      return [...new URLSearchParams(queryString).entries()].reduce(
        (q, [k, v]) => Object.assign(q, { [k]: v }),
        {} as T,
      );
    };

    const waitForQueryParams = async <T extends Object>() => {
      if (abortablePromise) {
        abortablePromise.abort();
      }

      await Bindings.web.clearAuthToken();

      abortablePromise = AbortablePromise.from(_doLoop<T>());

      return (await abortablePromise) as AbortablePromise<T>;
    };

    const tryLogin = async (url: string = loginUrl.value) => {
      $track('login_attempted');

      const { challenge, verifier } = await generatePKCE();
      const port = await startResponseServer();

      const u = new URL(url);
      u.searchParams.set('_', Date.now().toString());
      u.searchParams.set('skip_splash', true.toString());
      u.searchParams.set('origin', 'obs-plugin');
      u.searchParams.set('code_challenge', challenge);
      u.searchParams.set('port', port.toString());
      u.searchParams.set('code_flow', 'true');
      u.searchParams.set('external', 'electron');

      openBrowserTo(u.toString());

      try {
        const queryParams = await waitForQueryParams<{
          success: 'true';
          code: string;
        }>();

        const response = await fetchPkceCredentials(verifier, queryParams.code);
        if (!response.data.value) return;
        authedAt.value = Date.now();
        // Refresh token logic handled in authResponseParams watcher
        authResponseParams.value = response.data.value?.data;
        $track('login_successful');
        await Bindings.obs.bring_front();
        await app.bringToFront();
      } catch (err) {
        console.error(err);
      }
    };

    const setAccessToken = (tokenInfo: AuthResponseParams['api_token']) => {
      if (authResponseParams.value?.api_token) {
        authResponseParams.value.api_token = {
          ...authResponseParams.value.api_token,
          ...tokenInfo,
        };
      } else if (authResponseParams.value) {
        authResponseParams.value = {
          ...authResponseParams.value,
          api_token: tokenInfo,
        };
      }

      if (authResponseParams.value?.api_token) {
        setRefresh(tokenInfo.expires_at, tokenInfo.refresh_token);
      }
    };

    const setRefresh = (
      expiresAtInSecs: number,
      refreshToken: string,
      force?: boolean,
    ) => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      const ONE_HOUR_BEFORE = 60 * 60;
      const timeout = expiresAtInSecs * 1000 - Date.now() - ONE_HOUR_BEFORE;

      console.debug(
        'Auth token will be refreshed on',
        new Date(Date.now() + timeout),
      );

      refreshTimeout = setTimeout(
        async () => {
          const r = await api.auth.refreshToken(refreshToken);

          if (r.data.value) {
            setAccessToken(r.data.value);
          }

          return r;
        },
        force ? 0 : timeout,
      );
    };

    const getLoginToken = async (): Promise<MagicLoginToken | undefined> => {
      const res = await api.post('api/v5/obs-plugin/login/token'); // -> { login_token }
      // TODO: SE importer had this fallback, I've only seem `res.data.login_token`, but keeping it
      const loginToken = res.data.data?.login_token ?? res.data.login_token;
      return loginToken;
    };

    const username = computed(() => userInfo.value?.display_name);
    const isUltra = computed(() => userInfo.value?.ultra);
    const avatar = computed(() => ({
      url: userInfo.value?.avatar,
    }));

    const tipPageUrl = computed(() => {
      if (!donationSettings.value?.donation_url) {
        return undefined;
      }

      return donationSettings.value.donation_url.replace(
        'beta.streamlabs',
        'streamlabs',
      );
    });

    const isAuthenticated = computed(() => !!authResponseParams.value);

    const sendAuthToDocks = (
      docks: PDock[],
      authResponse: AuthResponseParams,
    ) => {
      console.groupCollapsed('sendAuthToDocks');

      for (const dock of docks) {
        if (!dock.isSlabs) {
          continue;
        }

        console.debug(dock.title, dock.url);

        if (!dock.url?.includes('loading=1')) {
          continue;
        }

        console.debug(`sending payload to dock: ${dock.name}`, authResponse);

        dock.executeJavascript(
          `window.consumeAuthResponse(${JSON.stringify(authResponse)});`,
        );
      }

      console.groupEnd();
    };

    // If store still has old token and doesn't have a new one yet
    // fetch the new token and set it
    // @returns The new api_token if refreshed, unchanged params
    const tryLegacyTokenExchange = async (
      params: AuthResponseParams,
    ): Promise<AuthResponseParams> => {
      if (params.oauth_token && !params.api_token) {
        console.debug('Trying token exchange');
        return await api.auth
          .legacyTokenExchange(params.oauth_token)
          .then((resp) => {
            if (resp.data?.data) {
              const token = resp.data.data;
              if (token) {
                setAccessToken(token);
              }
              return token;
            }
          })
          .catch(() => {
            // If token exchange fails, logout user, nothing good comes from keeping `authResponseParams`
            console.debug(
              'Logging out user because we tried to exchange legacy token and failed',
            );
            authResponseParams.value = undefined;
          });
      }

      return params;
    };

    watch(
      [
        () => obs.docks,
        authResponseParams, //
      ],
      (
        [
          docksAre,
          authResponseParamsAre, //
        ],
        [
          docksWere,
          authResponseParamsWere, //
        ],
      ) => {
        if (docksAre && authResponseParamsAre) {
          sendAuthToDocks(docksAre, authResponseParamsAre);
        }

        // we were authed, now we are not
        if (!authResponseParamsAre && authResponseParamsWere) {
          console.debug(`!authResponseParamsAre && authResponseParamsWere`);
          if (refreshTimeout) {
            clearTimeout(refreshTimeout);
          }
          reloadPluginDocks();
          return;
        }

        // we already had docks, so we've initialized already
        // this prevents infinite reloading
        if (docksWere) {
          console.debug(`docksWere`);
          return;
        }

        // docks are not loaded yet
        if (!docksAre) {
          console.debug(`!docksAre`);
          return;
        }

        // check if our docks exist
        if (!docksAre.find((dock) => dock.isSlabs)) {
          console.debug(`our docks don't exists, restoring!`);
          restoreDocks();
        } else {
          console.debug(`our docks exist, reloading them`);
          reloadPluginDocks();
        }
      },
      {
        immediate: true,
        // debounce: 350,
      },
    );

    const getMultistreamChatUrl = (
      platforms: MultistreamChatSupportedPlatform[],
    ) => {
      if (!authResponseParams.value?.api_token?.access_token) {
        return undefined;
      }

      const url = new URL(`https://streamlabs.com/embed/chat`);

      url.searchParams.set(
        'access_token',
        authResponseParams.value.api_token.access_token,
      );
      url.searchParams.set('mode', 'night');
      url.searchParams.set('mobile', '0');
      url.searchParams.set('platforms', platforms.join(','));
      url.searchParams.set('send', 'true');

      return url.toString();
    };

    const socketToken = computed(() => userInfo.value?.socket_token);
    const socket = ref<ReturnType<typeof io>>();
    const socketStatus = ref<
      'CONNECTING' | 'CONNECTED' | 'FAILED_TO_CONNECT'
    >();

    watch(
      socketToken,
      (is, was) => {
        if (is) {
          socketStatus.value = 'CONNECTING';

          socket.value = io(
            `${$env.STREAMLABS_WS_BASE_PATH}/?token=${socketToken.value}`,
            {
              // forceNew: true,
              transports: ['websocket'],
              reconnection: true,
              reconnectionAttempts: 4,
              reconnectionDelay: 15,
            },
          );

          socket.value.on('connect_error', () => {
            $track('socket_connect_error');

            socketStatus.value = 'FAILED_TO_CONNECT';
            toast({
              variant: 'destructive',
              title: 'Websocket Connection Error',
              description: `We we're unable to connect to the Streamlabs Websocket. Please restart OBS and contact support if the issue persist.`,
              duration: 60000,
            });
          });

          socket.value.on('connect', () => {
            socketStatus.value = 'CONNECTED';
          });

          socket.value.on('event', (d: StreamlabsWebsocketEvent) => {
            emitter.emit('event', d);

            console.debug('socket event', d);

            switch (d.type) {
              case 'donation':
                emitter.emit('donation', { for: d.for, message: d.message });
                break;
              case 'streamlabels':
                emitter.emit('streamlabels', { message: d.message.data });
                break;
              case 'streamlabs_prime_subscribe':
                emitter.emit('streamlabs_prime_subscribe', {
                  message: d.message.data,
                });
                break;
              case 'multistreamLive':
                emitter.emit('multistreamLive', d.data);
                break;
              case 'streamElementsImportComplete':
                emitter.emit('streamElementsImportComplete', d.data);
                break;
              case 'account_permissions_required':
                emitter.emit('account_permissions_required', d);
                break;
              case 'account_merge_error':
                emitter.emit('account_merge_error', d);
                break;
              // Deliberately choosing to keep them split, even though they might have the same handler
              case 'visionEvent':
                emitter.emit('visionEvent', d);
                break;
              case 'userStateUpdated':
                emitter.emit('userStateUpdated', d);
                break;
            }
          });
        }
      },
      {
        immediate: true,
      },
    );

    const ensureUltra = (
      reason: DialogProps['ULTRA_REQUIRED']['reason'],
      params: DialogProps['ULTRA_REQUIRED']['params'],
    ) => {
      if (!isUltra.value) {
        dialogs.showDialog('ULTRA_REQUIRED', { reason, params });

        $track('ultra_upsell_shown', { reason });

        return false;
      }

      return true;
    };

    emitter.on('streamlabs_prime_subscribe', () => {
      console.debug(`emitter.on("streamlabs_prime_subscribe")`);
      $track('ultra_subscriptions');
      // refresh user data, they joined Ultra
      fetchUserInfo().then(({ data }) => {
        userInfo.value = data.value;
      });
    });

    emitter.on('streamElementsImportComplete', ({ settings_id }) => {
      api
        .get(
          `api/v5/importer/obs-plugin/streamelements/scene-collections/${settings_id}`,
        )
        .then((collections) => {
          if (!collections.data.data) return;
          library.installSEOverlays(
            Object.values(collections.data.data.scene_collections),
          );
        });
    });

    const getMagicUrl = async (url: string) => {
      const loginToken = await getLoginToken();

      if (!loginToken) {
        return;
      }

      return getMagicSessionUrlFor(url, loginToken);
    };

    return {
      isAuthenticated,
      getMultistreamChatUrl,
      username,
      avatar,
      isUltra,
      logout,
      tryLogin,
      // TODO: expose getMagicUrl only, after migrating SE importer
      getLoginToken,
      getMagicUrl,
      setAccessToken,

      ensureUltra,

      isBetaTester,
      authResponseParams,
      authedAt,
      expiresAt,
      userInfo,
      donationSettings,
      tipPageUrl,

      socket: emitter,
      socketStatus,

      uuid,

      waitForQueryParams,
      startResponseServer,

      dispose() {
        // ...
      },
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
    },
  },
);

export const useAuthStore = useStore;

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    useStore().dispose();
  });

  import.meta.hot.accept(acceptHMRUpdate(useStore, import.meta.hot));
}
